import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface Params {
  quoteId: string;
  clientId: string;
}

interface APIResponse {
  electronic_signature_url?: string;
  status?: string;
  error?: string;
}

export interface SigningDetails {
  electronicSignatureUrl?: string;
  status?: string;
  error?: string;
}

interface CustomOptions {
  onError?: (error: unknown) => void;
  onSuccess?: (response: SigningDetails) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/quotes/shared`;

export function fetchSigningDetailsKey(quoteId: string, clientId: string): QueryKey {
  return ['signing-details', quoteId, clientId] as QueryKey;
}

export async function fetchSigningDetails(params: Params, customOptions: CustomOptions) {
  const { onError, onSuccess } = customOptions;

  try {
    const response = await qontoClient<APIResponse>(
      `${URL}/${params.quoteId}/signing/${params.clientId}`
    );
    const camelizedResponse = camelizeKeys(response);

    if (onSuccess) {
      onSuccess(camelizedResponse);
    }
    return camelizedResponse;
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchSigningDetailsResponse = Awaited<ReturnType<typeof fetchSigningDetails>>;

export function useFetchSigningDetails(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchSigningDetailsResponse>>
) {
  return useQuery({
    queryKey: fetchSigningDetailsKey(params.quoteId, params.clientId),
    queryFn: () => fetchSigningDetails(params, { ...customOptions }),
    enabled: Boolean(params.quoteId && params.clientId),
    ...queryOptions,
  });
}
